import { ReactNode, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    Toolbar,
    Typography,
    useMediaQuery,
    Breadcrumbs,
    Link,
    Avatar,
    Menu as MuiMenu,
    MenuItem,
    ListItemIcon,
    Divider,
    Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/MenuOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import { useLogout, useGetIdentity, useAuthState } from 'react-admin';
import { CustomMenu } from './Menu';
import { colors } from '../theme';

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 72;

interface LayoutProps {
    children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        return localStorage.getItem('sidebarCollapsed') === 'true';
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const location = useLocation();
    const logout = useLogout();
    const { data: identity } = useGetIdentity();
    const { isPending, authenticated } = useAuthState();

    const drawerWidth = collapsed && !isMobile ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;

    // If not authenticated or still checking, render children only (login page)
    if (isPending) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: colors.slate[50],
                }}
            >
                Loading...
            </Box>
        );
    }

    if (!authenticated) {
        return <>{children}</>;
    }

    const handleDrawerToggle = () => {
        if (isMobile) {
            setMobileOpen(!mobileOpen);
        } else {
            const newCollapsed = !collapsed;
            setCollapsed(newCollapsed);
            localStorage.setItem('sidebarCollapsed', String(newCollapsed));
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
    };

    // Generate breadcrumbs from path
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const getBreadcrumbTitle = (segment: string): string => {
        const titles: Record<string, string> = {
            'bootstrap-tokens': 'Bootstrap Tokens',
            'devices': 'Devices',
            'users': 'Users',
            'create': 'Create',
            'show': 'Details',
            'edit': 'Edit',
        };
        return titles[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    };

    const userInitial = identity?.fullName?.charAt(0).toUpperCase() ||
                        identity?.email?.charAt(0).toUpperCase() || 'U';
    const userName = identity?.fullName || identity?.email || 'User';

    const drawer = (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: `linear-gradient(180deg, ${colors.slate[900]} 0%, ${colors.slate[950]} 100%)`,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: collapsed && !isMobile ? '20px 12px' : '20px 24px',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                }}
            >
                <Box
                    component="img"
                    src="/images/xtconnect-logo.png"
                    alt="XTConnect"
                    sx={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        borderRadius: '10px',
                        objectFit: 'cover',
                    }}
                />
                {(!collapsed || isMobile) && (
                    <Box sx={{ transition: 'opacity 0.2s' }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: 'white',
                                letterSpacing: '-0.5px',
                                lineHeight: 1.2,
                            }}
                        >
                            XTConnect
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: colors.slate[400], fontSize: 12 }}
                        >
                            Administration
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Navigation */}
            <Box sx={{ flex: 1, overflow: 'auto', py: 3 }}>
                <CustomMenu collapsed={collapsed && !isMobile} />
            </Box>

            {/* Footer - User info */}
            <Box
                sx={{
                    p: collapsed && !isMobile ? '16px 12px' : '16px 24px',
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                }}
            >
                <Tooltip title={collapsed && !isMobile ? userName : ''} placement="right">
                    <Avatar
                        sx={{
                            width: 40,
                            height: 40,
                            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                            fontSize: 16,
                            fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                        }}
                    >
                        {userInitial}
                    </Avatar>
                </Tooltip>
                {(!collapsed || isMobile) && (
                    <>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: 'white',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {userName}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: colors.slate[400] }}>
                                TechSupport
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleLogout}
                            sx={{
                                color: colors.slate[400],
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    color: colors.error,
                                },
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </>
                )}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Sidebar */}
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                {/* Desktop drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    minHeight: '100vh',
                    backgroundColor: colors.slate[50],
                }}
            >
                {/* Top bar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(12px)',
                        borderBottom: `1px solid ${colors.slate[200]}`,
                        backgroundImage: 'none',
                        boxShadow: 'none',
                    }}
                >
                    <Toolbar sx={{ gap: 2 }}>
                        <IconButton
                            onClick={handleDrawerToggle}
                            sx={{
                                color: colors.slate[600],
                                '&:hover': { backgroundColor: colors.slate[100] },
                            }}
                        >
                            {collapsed && !isMobile ? <MenuIcon /> : <ChevronLeftIcon />}
                        </IconButton>

                        {/* Breadcrumbs */}
                        <Breadcrumbs
                            sx={{
                                '& .MuiBreadcrumbs-separator': {
                                    color: colors.slate[300],
                                },
                            }}
                        >
                            <Typography color={colors.slate[500]} fontSize={14}>
                                Admin
                            </Typography>
                            {pathSegments.map((segment, index) => {
                                const isLast = index === pathSegments.length - 1;
                                const path = '/' + pathSegments.slice(0, index + 1).join('/');

                                if (isLast) {
                                    return (
                                        <Typography
                                            key={path}
                                            sx={{
                                                color: colors.slate[700],
                                                fontWeight: 500,
                                                fontSize: 14,
                                            }}
                                        >
                                            {getBreadcrumbTitle(segment)}
                                        </Typography>
                                    );
                                }
                                return (
                                    <Link
                                        key={path}
                                        href={`#${path}`}
                                        underline="hover"
                                        sx={{
                                            color: colors.primary,
                                            fontWeight: 500,
                                            fontSize: 14,
                                        }}
                                    >
                                        {getBreadcrumbTitle(segment)}
                                    </Link>
                                );
                            })}
                        </Breadcrumbs>

                        <Box sx={{ flex: 1 }} />

                        {/* Current time */}
                        <Typography
                            sx={{
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 14,
                                color: colors.slate[500],
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            <CurrentTime />
                        </Typography>

                        <Tooltip title="Toggle dark mode">
                            <IconButton
                                sx={{
                                    color: colors.slate[600],
                                    '&:hover': { backgroundColor: colors.slate[100] },
                                }}
                            >
                                <DarkModeIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Refresh">
                            <IconButton
                                onClick={() => window.location.reload()}
                                sx={{
                                    color: colors.slate[600],
                                    '&:hover': { backgroundColor: colors.slate[100] },
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Account">
                            <IconButton onClick={handleMenuOpen}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: 14,
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                                    }}
                                >
                                    {userInitial}
                                </Avatar>
                            </IconButton>
                        </Tooltip>

                        <MuiMenu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={handleMenuClose}>
                                <ListItemIcon>
                                    <PersonIcon fontSize="small" />
                                </ListItemIcon>
                                Profile
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                Logout
                            </MenuItem>
                        </MuiMenu>
                    </Toolbar>
                </AppBar>

                {/* Page content */}
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

// Current time component
const CurrentTime = () => {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            {time.toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            })}
        </>
    );
};

export default Layout;
